import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import dayjs, { Dayjs } from "dayjs";
import { Theme } from "@mui/material/styles";
import { RootState } from "../store/reducers";
import transactionApi from "../store/modules/transactionYear/transactionApi";
import { TransactionStatus } from "../store/repsaApi";
import { NO_YEARS_SELECTED } from "../ui-components/YearFilterSelect";

export { TransactionStatus };

export const PIPELINE_ROUTE = "pipeline";

/** Lives in the Pipeline folder (Lead, Active, Under Contract) */
export const isPipelineStatus = (status?: TransactionStatus | null) =>
  status === TransactionStatus.Lead ||
  status === TransactionStatus.Active ||
  status === TransactionStatus.UnderContract;

/** Not under contract yet (Lead, Active) */
export const isLeadOrActive = (status?: TransactionStatus | null) =>
  status === TransactionStatus.Lead || status === TransactionStatus.Active;

/** A real deal (Under Contract, Closed) */
export const isContractedStatus = (status?: TransactionStatus | null) =>
  status === TransactionStatus.UnderContract ||
  status === TransactionStatus.Transaction;

export const TRANSACTION_STATUS_OPTIONS: {
  value: TransactionStatus;
  label: string;
  note?: string;
}[] = [
  { value: TransactionStatus.Lead, label: "Lead" },
  { value: TransactionStatus.Active, label: "Active", note: "Agency signed" },
  {
    value: TransactionStatus.UnderContract,
    label: "Under Contract",
    note: "Offer accepted",
  },
  { value: TransactionStatus.Transaction, label: "Closed" },
];

export const transactionStatusLabel = (status?: TransactionStatus) =>
  TRANSACTION_STATUS_OPTIONS.find((o) => o.value === status)?.label ??
  "Closed";

/** "Closing Date" for Closed entries, "Potential Closing Date" for Lead/Active/Under Contract */
export const closingDateLabel = (status?: TransactionStatus | null) =>
  isPipelineStatus(status) ? "Potential Closing Date" : "Closing Date";

/** Potential Closing Date already in the past */
export const isOverdueEntry = (
  status?: TransactionStatus | null,
  date?: Dayjs | string | null,
) => isPipelineStatus(status) && !!date && dayjs(date).isBefore(dayjs(), "day");

/** Colour of the "+ Active" dots (same as the GCI tracker) and of the Active status */
export const ACTIVE_DOT_COLOR = "#8e24aa";

/** Status chip colours: Active matches the "+ Active" dots, Under Contract the "+ Under Contracts" dots */
export const statusColor = (theme: Theme, status?: TransactionStatus | null) => {
  switch (status) {
    case TransactionStatus.Active:
      return ACTIVE_DOT_COLOR;
    case TransactionStatus.UnderContract:
      return theme.palette.warning.main;
    case TransactionStatus.Lead:
      return theme.palette.grey[600];
    default:
      return undefined;
  }
};

/** sx for a filled status chip */
export const statusChipSx = (theme: Theme, status?: TransactionStatus | null) => {
  const color = statusColor(theme, status);
  return color
    ? { bgcolor: color, color: theme.palette.getContrastText(color) }
    : {};
};

interface ChartYear {
  id?: number;
  year?: number;
}

/** An analytics row with the flags that split it between the line and the dots */
interface AnalyticsRow {
  yearId?: number;
  year?: number;
  isPipeline?: boolean;
  isUnderContract?: boolean;
}

/** Year folders for analytics charts, and the Pipeline folder id */
export const useAnalyticsYears = () => {
  const { years } = useSelector((state: RootState) => state.transactionYear);
  const { data: pipelineFolder } = usePipelineFolder();
  return {
    years,
    pipelineYearId: pipelineFolder?.id,
  };
};

/** Axis entry for a year that has Pipeline entries but no year folder yet (e.g. 2027) */
export const isFolderlessYearId = (yearId?: number) =>
  yearId !== undefined && yearId < NO_YEARS_SELECTED;

/**
 * Splits analytics rows into the regular line ("actual", Closed entries) and the
 * "+ Under Contracts" and "+ Active" dot sets, and works out the years on the X axis.
 * - The X axis is years only. Every Active entry counts (the server already leaves out
 *   Lead entries and overdue ones, whose Potential Closing Date has passed), whatever years
 *   are selected: it goes to the year of its Potential Closing Date, taking that year folder's
 *   id, or an axis entry of its own for a year with no folder (e.g. 2027). Such years join the axis.
 * - With "Under Contracts" checked the API returns every Under Contract entry (Pipeline folder),
 *   whatever years are selected; they are placed by year the same way and their years join the axis too.
 * - On every time axis (years, quarters or months), Active and Under Contracts are "+"
 *   dot series on top of the line, one per period, never an extra axis entry.
 * - A year on the axis only for its dots (not selected, or no folder) has no line; with no
 *   year folder selected there is no line at all.
 */
export function splitAnalyticsRows<T extends AnalyticsRow>(
  rows: T[] | null | undefined,
  options: {
    years: ChartYear[];
    selectedYearIds?: number[];
    pipelineYearId?: number;
    includeUnderContracts?: boolean;
  },
) {
  const { pipelineYearId, includeUnderContracts = false } = options;
  const data = rows ?? [];
  const selected = options.selectedYearIds ?? [];
  const years = options.years.filter((y) => y.id !== pipelineYearId);
  const includePipeline =
    pipelineYearId !== undefined && selected.includes(pipelineYearId);

  const allYears = selected.length === 0;
  const realSelected = selected.filter(
    (id) => id !== pipelineYearId && id !== NO_YEARS_SELECTED,
  );
  const noRealYears = !allYears && realSelected.length === 0;
  // Years whose own Transactions are fetched, so they have a line
  const lineYearIds = allYears ? years.map((y) => y.id!) : realSelected;

  const extraYears: ChartYear[] = [];
  // Pipeline folder rows go to the year folder of their (Potential) Closing Date
  const byYear = (row: T): T => {
    const folder = years.find((y) => y.year === row.year);
    const id = folder ? folder.id! : -row.year!;
    if (!folder && !extraYears.some((y) => y.id === id)) {
      extraYears.push({ id, year: row.year });
    }
    return { ...row, yearId: id };
  };
  const pipeline: T[] = includePipeline
    ? data.filter((row) => row.isPipeline && row.year).map(byYear)
    : [];

  const underContract: T[] = includeUnderContracts
    ? data
        .filter((row) => !row.isPipeline && row.isUnderContract && row.year)
        .map(byYear)
    : [];

  const chartYears = [...years, ...extraYears];
  const axisIds = Array.from(
    new Set([
      ...lineYearIds,
      ...pipeline.map((row) => row.yearId!),
      ...underContract.map((row) => row.yearId!),
    ]),
  );
  const yearOf = (id: number) => chartYears.find((y) => y.id === id)?.year ?? 0;
  const periodYearIds = axisIds.sort((a, b) => yearOf(a) - yearOf(b));

  return {
    /** Real year folders plus axis entries for later years without a folder */
    chartYears,
    /** Year ids on the X axis, oldest first */
    periodYearIds,
    /** One year on the axis by selection (quarters or months of that year) */
    isSingleYear: !allYears && periodYearIds.length === 1,
    /** A year on the axis only for its dots (not selected, or no folder) has no line */
    isDotsOnlyYear: (yearId?: number) =>
      yearId === undefined || !lineYearIds.includes(yearId),
    // Whether there are "+ Active" / "+ Under Contracts" dots to draw (and list in the legend)
    includePipeline: pipeline.length > 0,
    includeUnderContracts: underContract.length > 0,
    // With no year folder selected there is no line, only the Active / Under Contracts dots
    actual: noRealYears
      ? []
      : data.filter((row) => !row.isPipeline && !row.isUnderContract),
    underContract,
    pipeline,
  };
}

/** The user's Pipeline folder (its year id, entry and overdue counts) */
export const usePipelineFolder = () => {
  const { selectedCoAgentIds } = useSelector(
    (state: RootState) => state.coAgentFilter,
  );
  return useQuery({
    queryKey: ["pipelineFolder", selectedCoAgentIds],
    queryFn: () => transactionApi.getPipelineFolder(),
  });
};
